// src/pages/lkp/EntityCrosswalk.tsx
// Step 3 layer for an entity: the canonical value set + the source→canonical crosswalk,
// shown as three buckets (unmapped source / mapped / canonical).
import React, { useState } from "react";
import type { LookupEntity, Buckets } from "../api/lkpEntityAPI";
import { Section, type EntityAct } from "./entityShared";

/** Section wrapper + the three-bucket crosswalk grid. */
export function CrosswalkSection({ entity, buckets, busy, act }:
  { entity: LookupEntity; buckets: Buckets | null; busy: string | null; act: EntityAct }) {
  return (
    <><style>{`
      .xw-setall{margin-left:8px;font-size:11px;border:1px solid #cbd5e0;border-radius:6px;padding:2px 6px;color:#5f6e7d;cursor:pointer}
      .xw-cselect{font-size:11px;border:1px solid #d3d9e0;border-radius:6px;padding:2px 6px;cursor:pointer;font-weight:600;color:#fff}
      .xw-cselect.cs-approved{background:#2f7a4f;border-color:#2f7a4f}
      .xw-cselect.cs-obsolete{background:#b23a30;border-color:#b23a30}
      .xw-cselect.cs-draft{background:#b8860b;border-color:#b8860b}
      .xw-cselect.cs-candidate{background:#43607e;border-color:#43607e}
      .xw-cselect.cs-submittedforapproval{background:#6a5acd;border-color:#6a5acd}
      .xw-cselect.cs-notdocumentedyet{background:#8a97a3;border-color:#8a97a3}
      .xw-cselect option{background:#fff;color:#1a2b3c}
    `}</style>
    <Section title="Canonical values & crosswalk"
      hint="Author the governed set, then map every source value to a canonical code (harmonization)."
      right={<button className="btn line" disabled={!!busy} onClick={() => act("auto_seed", {}, "auto_seed")}>{busy === "auto_seed" ? "…" : "Auto-map exact matches"}</button>}>
      <Crosswalk e={entity} buckets={buckets} canonCodes={entity.canonical_values.map((c) => c.code)} act={act} busy={busy} />
    </Section>
    </>
  );
}

function Crosswalk({ e, buckets, canonCodes, act, busy }: {
  e: LookupEntity; buckets: Buckets | null; canonCodes: string[]; act: EntityAct; busy: string | null;
}) {
  const [newCode, setNewCode] = useState(""); const [newDisp, setNewDisp] = useState("");
  if (!buckets) return <div className="muted">Loading…</div>;
  const b = buckets;
  return (
    <div className="xw">
      <style>{`.xw-promoteall{margin-left:8px;border:1px solid var(--gov);background:var(--gov);color:#fff;border-radius:6px;padding:2px 9px;font-size:11px;font-weight:600;cursor:pointer;font-family:inherit}.xw-promoteall:disabled{opacity:.5;cursor:default}`}</style>
      <div className="xw-cols">
        {/* unmapped source = work queue */}
        <div className="xw-col">
          <div className="xw-h warn">Unmapped source <span className="xw-n">{b.counts.unmapped_source}</span>
            {b.counts.unmapped_source > 0 && <button className="xw-promoteall" disabled={!!busy} title="make every remaining data value a canonical value (profiling-governed)"
              onClick={() => act("promote_all_source", {}, "promote_all")}>{busy === "promote_all" ? "…" : "Promote all →"}</button>}</div>
          <div className="xw-hint">seen in data, no canonical yet — the work queue. No client data? “Promote all” makes the data values your canonical set.</div>
          {b.unmapped_source.map((u) => (
            <div key={u.source_code} className="xw-row">
              <span className="mono xw-code bad">{u.source_code}</span>
              {u.display ? <span className="xw-disp">{u.display}</span> : null}
              <div className="sp" />
              <select className="xw-sel" disabled={!!busy} defaultValue=""
                onChange={(ev) => { if (ev.target.value) act("map_crosswalk", { source_code: u.source_code, canonical_code: ev.target.value }, "map"); }}>
                <option value="" disabled>map to…</option>
                {canonCodes.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <button className="mini pri" disabled={!!busy} title="make this a canonical value as-is"
                onClick={() => act("promote_source", { source_code: u.source_code }, "promote")}>Promote</button>
            </div>
          ))}
          {b.unmapped_source.length === 0 && <div className="xw-ok">All source values mapped ✓</div>}
        </div>

        {/* mapped */}
        <div className="xw-col">
          <div className="xw-h gov">Mapped <span className="xw-n">{b.counts.mapped}</span></div>
          <div className="xw-hint">source → canonical</div>
          {b.mapped.map((m) => (
            <div key={m.source_code} className="xw-row">
              <span className="mono xw-code">{m.source_code}</span>
              <span className="xw-arrow">→</span>
              <span className="mono xw-canon">{m.canonical_code}</span>
              {m.implicit && <span className="xw-imp">exact</span>}
            </div>
          ))}
          {b.mapped.length === 0 && <div className="muted small">none yet</div>}
        </div>

        {/* canonical set */}
        <div className="xw-col">
          <div className="xw-h">Canonical set <span className="xw-n">{b.counts.canonical}</span>
            <select className="xw-setall" disabled={!!busy} value=""
              onChange={(ev) => { const v = ev.target.value; if (v) act("set_all_canonical_status", { status: v }, "setall"); ev.target.value = ""; }}
              title="Set the governance status for ALL values in this field">
              <option value="">Set all to…</option>
              <option value="Approved">Approved</option>
              <option value="Obsolete">Obsolete</option>
              <option value="Draft">Draft</option>
              <option value="Candidate">Candidate</option>
              <option value="Submitted For Approval">Submitted For Approval</option>
              <option value="Not Documented Yet">Not Documented Yet</option>
            </select>
          </div>
          <div className="xw-hint">the governed values</div>
          {e.canonical_values.map((c) => {
            const unused = b.unused_canonical.some((u) => u.code === c.code);
            return (
              <div key={c.code} className="xw-row">
                <span className={"mono xw-code canon " + (c.governance_status === "Obsolete" ? "dep" : "")}>{c.code}</span>
                {c.display ? <span className="xw-disp">{c.display}</span> : null}
                <span className="xw-prov">{c.provenance === "client" ? "client" : c.provenance === "profiling_promoted" ? "profile" : "manual"}</span>
                {unused && <span className="xw-unused" title="governed but never seen in data">unused</span>}
                <div className="sp" />
                {/* per-value governance status dropdown (replaces Deprecate) */}
                <select className={"xw-cselect cs-" + (c.governance_status || "").toLowerCase().replace(/[^a-z]/g, "")}
                  disabled={!!busy} value={c.governance_status || ""}
                  title={c.description || "governance status"}
                  onChange={(ev) => act("set_canonical_status", { code: c.code, status: ev.target.value })}>
                  <option value="" disabled>— status —</option>
                  <option value="Approved">Approved</option>
                  <option value="Obsolete">Obsolete</option>
                  <option value="Draft">Draft</option>
                  <option value="Candidate">Candidate</option>
                  <option value="Submitted For Approval">Submitted For Approval</option>
                  <option value="Not Documented Yet">Not Documented Yet</option>
                </select>
              </div>
            );
          })}
          <div className="xw-add">
            <input className="mono" placeholder="code" value={newCode} onChange={(ev) => setNewCode(ev.target.value)} />
            <input placeholder="display" value={newDisp} onChange={(ev) => setNewDisp(ev.target.value)} />
            <button className="mini pri" disabled={!!busy || !newCode.trim()}
              onClick={() => { act("add_canonical", { code: newCode.trim(), display: newDisp.trim() || undefined }); setNewCode(""); setNewDisp(""); }}>Add</button>
          </div>
        </div>
      </div>
    </div>
  );
}